import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Building2, ListChecks, Flag, PauseCircle, XCircle, Lock, Car, CreditCard, ArrowLeftRight, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useStatuses } from "@/context/StatusContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Anagrafiche() {
  const { reload: reloadStatuses } = useStatuses();
  const [committenti, setCommittenti] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [attivita, setAttivita] = useState([]);
  const [statiTicket, setStatiTicket] = useState([]);
  const [motivi, setMotivi] = useState([]);
  const [koMotivi, setKoMotivi] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [carte, setCarte] = useState([]);
  const [groups, setGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [groupDlg, setGroupDlg] = useState(null);
  const [groupSel, setGroupSel] = useState([]);
  const [groupSearch, setGroupSearch] = useState("");
  const [prodQuery, setProdQuery] = useState("");
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState({});

  const load = () => {
    api.get("/committenti").then((r) => setCommittenti(r.data));
    api.get("/categories").then((r) => setCategorie(r.data));
    api.get("/attivita-eseguite").then((r) => setAttivita(r.data));
    api.get("/ticket-statuses").then((r) => setStatiTicket(r.data));
    api.get("/motivi-sospensione").then((r) => setMotivi(r.data));
    api.get("/motivi-ko").then((r) => setKoMotivi(r.data));
    api.get("/vehicles").then((r) => setMezzi(r.data)).catch(() => {});
    api.get("/fuel-cards").then((r) => setCarte(r.data)).catch(() => {});
    api.get("/product-groups").then((r) => setGroups(r.data)).catch(() => {});
    api.get("/magazzino/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  };
  useEffect(load, []);

  const openGroup = (g) => { setGroupSel(g ? [...g.product_ids] : []); setGroupSearch(""); setGroupDlg({ item: g }); };
  const toggleGroupProduct = (pid) => setGroupSel((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);
  const saveGroup = async () => {
    if (groupSel.length < 2) { toast.error("Seleziona almeno 2 prodotti interscambiabili"); return; }
    try {
      if (groupDlg.item) await api.put(`/product-groups/${groupDlg.item.id}`, { product_ids: groupSel });
      else await api.post("/product-groups", { product_ids: groupSel });
      toast.success("Gruppo salvato"); setGroupDlg(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeGroup = async (id) => {
    if (!window.confirm("Eliminare il gruppo di prodotti interscambiabili?")) return;
    try { await api.delete(`/product-groups/${id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const prodLabel = (p) => `${p.sku ? p.sku + " · " : ""}${p.name || p.product_id}`;
  const groupOf = (pid) => groups.find((g) => (g.product_ids || []).includes(pid));

  const openNew = (type) => {
    setForm(type === "comm" ? { name: "", contact: "", notes: "" }
      : type === "cat" ? { name: "", color: "#2563eb", standard_price: 0 }
      : type === "stat" ? { label: "", color: "#2563eb" }
      : type === "veh" ? { name: "", plate: "", type: "auto" }
      : type === "card" ? { number: "", provider: "", plate: "", notes: "" }
      : { name: "" });
    setDlg({ type, item: null });
  };
  const openEdit = (type, item) => { setForm({ ...item }); setDlg({ type, item }); };

  const PATHS = { comm: "committenti", cat: "categories", att: "attivita-eseguite", stat: "ticket-statuses", motivo: "motivi-sospensione", motivoko: "motivi-ko", veh: "vehicles", card: "fuel-cards" };
  const save = async () => {
    const path = PATHS[dlg.type];
    let payload;
    if (dlg.type === "comm") payload = { name: form.name, contact: form.contact || "", notes: form.notes || "" };
    else if (dlg.type === "cat") payload = { name: form.name, color: form.color, standard_price: parseFloat(form.standard_price) || 0 };
    else if (dlg.type === "stat") payload = { label: form.label, color: form.color || "#64748b" };
    else if (dlg.type === "veh") payload = { name: form.name, plate: form.plate || "", type: form.type || "auto" };
    else if (dlg.type === "card") payload = { number: form.number, provider: form.provider || "", plate: form.plate || "", notes: form.notes || "" };
    else payload = { name: form.name };
    const invalid = dlg.type === "stat" ? !form.label : dlg.type === "card" ? !form.number : !form.name;
    if (invalid) { toast.error(dlg.type === "card" ? "Numero carta obbligatorio" : "Nome obbligatorio"); return; }
    try {
      if (dlg.item) await api.put(`/${path}/${dlg.item.id}`, payload);
      else await api.post(`/${path}`, payload);
      toast.success("Salvato"); setDlg(null); load();
      if (dlg.type === "stat") reloadStatuses();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (type, id) => {
    if (!window.confirm("Eliminare l'elemento?")) return;
    const path = PATHS[type];
    try { await api.delete(`/${path}/${id}`); toast.success("Eliminato"); load(); if (type === "stat") reloadStatuses(); } catch (e) { toast.error(apiError(e)); }
  };

  const Section = ({ title, items, type, render, locked }) => (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h3 className="font-bold text-navy">{title}</h3>
        <Button size="sm" variant="outline" onClick={() => openNew(type)} data-testid={`add-${type}`}><Plus size={16} />Aggiungi</Button>
      </div>
      <div className="divide-y divide-border">
        {items.length ? items.map((it) => (
          <div key={it.id} className="flex items-center justify-between px-6 py-3.5" data-testid={`${type}-row`}>
            {render(it)}
            {locked && locked(it) ? (
              <span className="flex items-center gap-1 text-xs font-semibold text-muted-foreground"><Lock size={13} />Fisso</span>
            ) : (
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(type, it)} className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-primary"><Pencil size={15} /></button>
                <button onClick={() => remove(type, it.id)} className="rounded p-1.5 text-muted-foreground hover:bg-red-50 hover:text-destructive"><Trash2 size={15} /></button>
              </div>
            )}
          </div>
        )) : <p className="px-6 py-8 text-center text-sm text-muted-foreground">Nessun elemento</p>}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader overline="Flux · Configurazione" title="Anagrafiche" subtitle="Gestisci committenti, categorie, attività, stati ticket, motivi, mezzi, carte carburante e prodotti interscambiabili" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="Committenti" items={committenti} type="comm" render={(it) => (
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><Building2 size={16} /></span>
            <div><p className="font-semibold text-navy">{it.name}</p>{it.contact && <p className="text-xs text-muted-foreground">{it.contact}</p>}</div>
          </div>
        )} />
        <Section title="Categorie intervento" items={categorie} type="cat" render={(it) => (
          <div className="flex items-center gap-3"><span className="h-3.5 w-3.5 rounded-full" style={{ background: it.color }} /><p className="font-semibold text-navy">{it.name}</p>{it.standard_price ? <span className="ml-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700">€ {it.standard_price}</span> : null}</div>
        )} />
        <Section title="Stati ticket" items={statiTicket} type="stat" locked={(it) => it.fixed} render={(it) => (
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${it.color}1a`, color: it.color }}><Flag size={16} /></span>
            <p className="font-semibold text-navy">{it.label}</p>
          </div>
        )} />
        <Section title="Motivi sospensione" items={motivi} type="motivo" render={(it) => (
          <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600"><PauseCircle size={16} /></span><p className="font-semibold text-navy">{it.name}</p></div>
        )} />
        <Section title="Motivi KO" items={koMotivi} type="motivoko" render={(it) => (
          <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-100 text-red-800"><XCircle size={16} /></span><p className="font-semibold text-navy">{it.name}</p></div>
        )} />
        <Section title="Attività eseguite" items={attivita} type="att" render={(it) => (
          <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><ListChecks size={16} /></span><p className="font-semibold text-navy">{it.name}</p></div>
        )} />
        <Section title="Mezzi (carburante)" items={mezzi} type="veh" render={(it) => (
          <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-navy"><Car size={16} /></span><div><p className="font-semibold text-navy">{it.name}</p><p className="text-xs text-muted-foreground">{it.plate || "—"} · {it.type}</p></div></div>
        )} />
        <Section title="Carte carburante" items={carte} type="card" render={(it) => (
          <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><CreditCard size={16} /></span><div><p className="font-semibold text-navy">{it.label || it.number}</p>{it.notes && <p className="text-xs text-muted-foreground">{it.notes}</p>}</div></div>
        )} />
      </div>

      {/* Prodotti interscambiabili */}
      <div className="rounded-xl border border-border bg-card" data-testid="interchange-section">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><ArrowLeftRight size={16} /></span>
            <div>
              <h3 className="font-bold text-navy">Prodotti interscambiabili</h3>
              <p className="text-xs text-muted-foreground">Prodotti equivalenti installabili l'uno al posto dell'altro: l'analisi giacenze per gli ODT considera l'intero gruppo.</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => openGroup(null)} data-testid="add-group"><Plus size={16} />Nuovo gruppo</Button>
        </div>
        <div className="divide-y divide-border">
          {groups.length ? groups.map((g) => (
            <div key={g.id} className="flex items-start justify-between gap-4 px-6 py-4" data-testid="group-row">
              <div className="flex flex-wrap gap-2">
                {g.products.map((p) => (
                  <span key={p.product_id} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                    {p.sku && <span className="font-mono text-indigo-500">{p.sku}</span>}
                    {p.name || p.product_id}
                  </span>
                ))}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => openGroup(g)} data-testid="group-edit" className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-primary"><Pencil size={15} /></button>
                <button onClick={() => removeGroup(g.id)} data-testid="group-delete" className="rounded p-1.5 text-muted-foreground hover:bg-red-50 hover:text-destructive"><Trash2 size={15} /></button>
              </div>
            </div>
          )) : <p className="px-6 py-8 text-center text-sm text-muted-foreground">Nessun gruppo. Crea un gruppo per associare prodotti interscambiabili.</p>}
        </div>
        {/* Verifica per prodotto: a quale gruppo appartiene */}
        <div className="border-t border-border px-6 py-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Verifica equivalenti per prodotto</p>
          <div className="relative mb-2">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cerca un prodotto per nome o SKU per vedere i suoi equivalenti..." value={prodQuery} onChange={(e) => setProdQuery(e.target.value)} data-testid="prod-lookup-search" />
          </div>
          {prodQuery.trim() && (
            <div className="max-h-72 divide-y divide-border/60 overflow-y-auto rounded-lg border border-border">
              {products.filter((p) => `${p.name} ${p.sku}`.toLowerCase().includes(prodQuery.toLowerCase())).slice(0, 30).map((p) => {
                const g = groupOf(p.id);
                const equis = g ? g.products.filter((x) => x.product_id !== p.id) : [];
                return (
                  <div key={p.id} className="flex items-start justify-between gap-3 px-3 py-2.5 text-sm" data-testid={`prod-lookup-row-${p.sku}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-navy">{p.sku && <span className="font-mono text-xs text-muted-foreground">{p.sku} · </span>}{p.name}</p>
                    </div>
                    {g ? (
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {equis.length ? equis.map((e) => (
                          <span key={e.product_id} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700"><ArrowLeftRight size={10} />{e.name}{e.sku ? ` · ${e.sku}` : ""}</span>
                        )) : <span className="text-xs text-muted-foreground">gruppo di 1</span>}
                      </div>
                    ) : <span className="shrink-0 text-xs text-muted-foreground">Nessun gruppo</span>}
                  </div>
                );
              })}
              {products.filter((p) => `${p.name} ${p.sku}`.toLowerCase().includes(prodQuery.toLowerCase())).length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">Nessun prodotto trovato.</p>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!groupDlg} onOpenChange={(o) => !o && setGroupDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{groupDlg?.item ? "Modifica" : "Nuovo"} gruppo interscambiabile</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            {groupSel.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="group-selected">
                {groupSel.map((pid) => { const p = products.find((x) => x.id === pid); return (
                  <button key={pid} onClick={() => toggleGroupProduct(pid)} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-700">
                    {p ? prodLabel(p) : pid} <XCircle size={13} />
                  </button>
                ); })}
              </div>
            )}
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Cerca prodotto per nome o SKU..." value={groupSearch} onChange={(e) => setGroupSearch(e.target.value)} data-testid="group-search" />
            </div>
            <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
              {products.filter((p) => `${p.name} ${p.sku}`.toLowerCase().includes(groupSearch.toLowerCase())).slice(0, 60).map((p) => {
                const sel = groupSel.includes(p.id);
                return (
                  <button key={p.id} onClick={() => toggleGroupProduct(p.id)} data-testid={`group-opt-${p.sku}`}
                    className={`flex w-full items-center justify-between gap-2 border-b border-border/50 px-3 py-2 text-left text-sm last:border-0 hover:bg-secondary ${sel ? "bg-indigo-50" : ""}`}>
                    <span className="flex items-center gap-2">
                      <span className={`flex h-4 w-4 items-center justify-center rounded border ${sel ? "border-indigo-600 bg-indigo-600 text-white" : "border-muted-foreground/40"}`}>{sel && "✓"}</span>
                      <span>{p.sku && <span className="font-mono text-xs text-muted-foreground">{p.sku} · </span>}{p.name}</span>
                    </span>
                  </button>
                );
              })}
              {products.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">Catalogo Magazzino non disponibile.</p>}
            </div>
            <p className="text-xs text-muted-foreground">Seleziona almeno 2 prodotti. Se uno appartiene già a un gruppo, i gruppi verranno uniti automaticamente.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setGroupDlg(null)}>Annulla</Button><Button onClick={saveGroup} data-testid="group-save">Salva gruppo</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{dlg?.item ? "Modifica" : "Nuovo"} {dlg?.type === "comm" ? "committente" : dlg?.type === "cat" ? "categoria" : dlg?.type === "stat" ? "stato ticket" : dlg?.type === "motivo" ? "motivo sospensione" : dlg?.type === "motivoko" ? "motivo KO" : dlg?.type === "veh" ? "mezzo" : dlg?.type === "card" ? "carta carburante" : "attività eseguita"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            {dlg?.type === "stat" ? (
              <>
                <div className="space-y-1.5"><Label>Nome stato *</Label><Input data-testid="anag-name" value={form.label || ""} onChange={(e) => setForm({ ...form, label: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Colore</Label><input type="color" value={form.color || "#2563eb"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-full cursor-pointer rounded-lg border border-border" /></div>
              </>
            ) : dlg?.type === "card" ? (
              <>
                <div className="space-y-1.5"><Label>Numero carta *</Label><Input data-testid="anag-card-number" value={form.number || ""} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="es. 7083-1234" /></div>
                <div className="space-y-1.5"><Label>Gestore</Label><Input data-testid="anag-card-provider" value={form.provider || ""} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="es. ENI, Q8, IP" /></div>
                <div className="space-y-1.5"><Label>Targa associata</Label><Input data-testid="anag-card-plate" value={form.plate || ""} onChange={(e) => setForm({ ...form, plate: e.target.value })} placeholder="facoltativa" /></div>
                <div className="space-y-1.5"><Label>Note</Label><Input data-testid="anag-card-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </>
            ) : (
              <>
                <div className="space-y-1.5"><Label>Nome *</Label><Input data-testid="anag-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                {dlg?.type === "comm" ? (
                  <>
                    <div className="space-y-1.5"><Label>Contatto</Label><Input value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Note</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                  </>
                ) : dlg?.type === "cat" ? (
                  <>
                    <div className="space-y-1.5"><Label>Colore</Label><input type="color" value={form.color || "#2563eb"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-full cursor-pointer rounded-lg border border-border" /></div>
                    <div className="space-y-1.5"><Label>Prezzo standard (€)</Label><Input type="number" min="0" step="0.5" data-testid="anag-price" value={form.standard_price ?? 0} onChange={(e) => setForm({ ...form, standard_price: e.target.value })} /><p className="text-xs text-muted-foreground">Ricavo per intervento completato di questa categoria (pagina Risultati).</p></div>
                  </>
                ) : dlg?.type === "veh" ? (
                  <>
                    <div className="space-y-1.5"><Label>Targa</Label><Input data-testid="anag-veh-plate" value={form.plate || ""} onChange={(e) => setForm({ ...form, plate: e.target.value })} placeholder="es. AB123CD" /></div>
                    <div className="space-y-1.5"><Label>Tipo</Label>
                      <Select value={form.type || "auto"} onValueChange={(v) => setForm({ ...form, type: v })}>
                        <SelectTrigger data-testid="anag-veh-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{["auto", "furgone", "camion", "macchinario", "muletto", "altro"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </>
                ) : null}
              </>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="anag-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

